import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { ArrowRight, BarChart3, Brain, Database, Globe2, LineChart, Search, Sparkles, Table2, TrendingUp } from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";
type Metrics = {
  revenue: number;
  orders: number;
  conversion: number;
  byRegion: Array<{ region: string; revenue: number; orders: number }>;
  byMonth: Array<{ month: string; revenue: number; orders: number }>;
};

const copy = {
  zh: {
    brand: "启序数析",
    lang: "EN",
    nav: ["看板", "问数", "治理"],
    label: "独立案例站点 05 / 数据分析仪表盘 + 自然语言查询",
    title: "一个能把业务问题转成 SQL 和洞察的数据网站。",
    subtitle: "面向管理层、运营和销售团队，用自然语言查询销售数据，展示可审计 SQL、查询结果和 MiniMax 生成的业务解读。",
    primary: "开始问数",
    secondary: "查看看板",
    dashboardTitle: "经营指标看板",
    dashboardSubtitle: "内置模拟销售数据集，用于演示收入、订单、转化率和区域贡献。",
    queryTitle: "自然语言问数",
    querySubtitle: "后端根据问题选择受限查询，再直连 MiniMax 生成解释，避免直接执行任意 SQL。",
    question: "哪个区域收入最高？华东最近几个月趋势怎么样？",
    run: "生成 SQL 和洞察",
    sample: "填入示例",
    sql: "生成 SQL",
    insight: "AI 业务洞察",
    waiting: "等待输入业务问题。",
    governanceTitle: "数据治理边界",
    governanceSubtitle: "真实问数系统必须限制查询范围、保留 SQL 审计，并明确指标口径。",
    panels: [
      ["受限查询", "只允许访问白名单数据集和聚合字段，避免任意 SQL 风险。"],
      ["口径透明", "返回 SQL、结果表和指标解释，业务可以复核。"],
      ["洞察落地", "输出原因假设和下一步动作，而不只是展示数字。"]
    ],
    footer: "启序科技 AI 案例站点 · 第 5 个真实独立网站"
  },
  en: {
    brand: "Qixu Data Analyst",
    lang: "中文",
    nav: ["Dashboard", "Ask", "Govern"],
    label: "Standalone Case Site 05 / Data Dashboard + Natural Language Query",
    title: "A data site that turns business questions into SQL and insight.",
    subtitle: "For executives, ops and sales teams: ask sales questions in natural language and review SQL, query results and MiniMax analysis.",
    primary: "Ask data",
    secondary: "View dashboard",
    dashboardTitle: "Business Dashboard",
    dashboardSubtitle: "Built-in demo sales dataset for revenue, orders, conversion and regional contribution.",
    queryTitle: "Natural Language Query",
    querySubtitle: "The backend selects restricted queries, then calls MiniMax directly for business interpretation.",
    question: "Which region has the highest revenue? How is East China trending?",
    run: "Generate SQL and insight",
    sample: "Use sample",
    sql: "Generated SQL",
    insight: "AI business insight",
    waiting: "Waiting for a business question.",
    governanceTitle: "Data Governance",
    governanceSubtitle: "A real analytics agent needs query limits, SQL audit trail and metric definitions.",
    panels: [
      ["Restricted query", "Only whitelisted datasets and aggregate fields are available."],
      ["Transparent metrics", "Return SQL, result rows and metric explanations for review."],
      ["Actionable insight", "Explain possible causes and next steps, not just numbers."]
    ],
    footer: "Qixu Technology AI case site · fifth real standalone website"
  }
};

function money(value: number) {
  return `¥${Math.round(value / 1000)}k`;
}

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [question, setQuestion] = useState(t.question);
  const [sql, setSql] = useState("--");
  const [rows, setRows] = useState<Array<Record<string, unknown>>>([]);
  const [insight, setInsight] = useState(t.waiting);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch("/api/metrics").then((r) => r.json()).then((data) => setMetrics(data.metrics));
  }, []);

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setQuestion(copy[next].question);
    setInsight(copy[next].waiting);
    setMeta("");
  }

  async function query() {
    if (!question.trim() || loading) return;
    setLoading(true);
    setInsight(locale === "zh" ? "正在生成 SQL 和洞察..." : "Generating SQL and insight...");
    setMeta("");
    try {
      const response = await fetch("/api/query", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, locale })
      });
      const data = await response.json();
      setSql(data.sql || "--");
      setRows(data.rows || []);
      setInsight(data.text || t.waiting);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setInsight(locale === "zh" ? "网络异常，无法完成问数。" : "Network error. Query unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  const maxMonth = Math.max(...(metrics?.byMonth.map((m) => m.revenue) || [1]));

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top"><span>数</span><strong>{t.brand}</strong></a>
        <nav><a href="#dashboard">{t.nav[0]}</a><a href="#query">{t.nav[1]}</a><a href="#governance">{t.nav[2]}</a></nav>
        <button className="lang" onClick={switchLocale}><Globe2 size={16} />{t.lang}</button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#query">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#dashboard">{t.secondary}</a>
          </div>
        </div>
        <div className="hero-board">
          <div className="board-head"><Brain size={22} />Text-to-SQL workflow</div>
          <div className="pipeline">
            <div><Search size={18} /> Natural language</div>
            <div><Database size={18} /> Guarded query</div>
            <div><Table2 size={18} /> Result table</div>
            <div><LineChart size={18} /> Business insight</div>
          </div>
        </div>
      </section>

      <section className="dashboard" id="dashboard">
        <div className="section-copy"><h2>{t.dashboardTitle}</h2><p>{t.dashboardSubtitle}</p></div>
        <div className="kpis">
          <article><span>Revenue</span><strong>{metrics ? money(metrics.revenue) : "--"}</strong><em>+18.6%</em></article>
          <article><span>Orders</span><strong>{metrics?.orders ?? "--"}</strong><em>+9.2%</em></article>
          <article><span>Conversion</span><strong>{metrics ? `${Math.round(metrics.conversion * 100)}%` : "--"}</strong><em>stable</em></article>
        </div>
        <div className="charts">
          <div className="chart-card">
            <h3>Monthly revenue</h3>
            {metrics?.byMonth.map((item) => (
              <div className="bar-row" key={item.month}><span>{item.month}</span><div><i style={{ width: `${(item.revenue / maxMonth) * 100}%` }} /></div><strong>{money(item.revenue)}</strong></div>
            ))}
          </div>
          <div className="chart-card">
            <h3>Region ranking</h3>
            {metrics?.byRegion.sort((a, b) => b.revenue - a.revenue).map((item) => (
              <div className="region-row" key={item.region}><span>{item.region}</span><strong>{money(item.revenue)}</strong><em>{item.orders} orders</em></div>
            ))}
          </div>
        </div>
      </section>

      <section className="query" id="query">
        <div className="desk">
          <div className="section-title"><BarChart3 size={22} /><div><h2>{t.queryTitle}</h2><p>{t.querySubtitle}</p></div></div>
          <textarea value={question} onChange={(event) => setQuestion(event.target.value)} />
          <div className="composer-actions">
            <button onClick={() => setQuestion(t.question)}>{t.sample}</button>
            <button className="primary" onClick={query} disabled={loading || !question.trim()}>{t.run}<Sparkles size={16} /></button>
          </div>
        </div>
        <div className="result-card">
          <h3>{t.sql}</h3>
          <pre>{sql}</pre>
          <div className="rows">
            {rows.slice(0, 5).map((row, index) => <code key={index}>{JSON.stringify(row)}</code>)}
          </div>
          <h3>{t.insight}</h3>
          <p>{insight}</p>
          {meta && <span>{meta}</span>}
        </div>
      </section>

      <section className="governance" id="governance">
        <div><h2>{t.governanceTitle}</h2><p>{t.governanceSubtitle}</p></div>
        <div className="govern-grid">
          {t.panels.map(([title, content]) => <article key={title}><TrendingUp size={20} /><h3>{title}</h3><p>{content}</p></article>)}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
